<script lang="ts">
  import CurrentUserStore from "@shared/svelte/Stores/CurrentUserStore";
  import DistributorsStore from "@shared/svelte/Stores/DistributorsStore";
  import LiquorLicenseInfoBar from "@retailer/svelte/ProductLines/LiquorLicenseInfoBar.svelte";

  import type { NavigationMenu } from "@shared/types/NavigationMenuItem";

  import { CtaEntity, GlobalNavMenuEntity } from "@shared/services/event_tracking/entities";
  import { EventTracking } from "@shared/services/event_tracking/events";

  import { href, setupMenus } from "@retailer/svelte/ProductLines/Navigation/helpers";
  import { isOnProductTypeHeaderPage } from "@retailer/services/liquor_license_service";
  import { page } from "@roxi/routify";
  import {
    GlobalNavMenu,
    NavMenu,
    NavList,
    NavLink,
    Divider,
    Flex,
    Text,
    Icon,
    BeverageBottle,
    LiquorGlassBottleSolid,
    MartiniSolid,
    BeverageCan,
    DeliveryTruckSolid,
  } from "@getprovi/craft";

  const { trackClick } = EventTracking;

  interface DistributorCatalog {
    label: string;
    url: string;
    distributors: Distributor[];
  }

  interface Distributor {
    label: string;
    integrated: boolean;
    distributor_id: number;
  }

  let menuItems: NavigationMenu = setupMenus()?.[0] || {};
  let distributorItem: DistributorCatalog = {
    label: "Distributor Catalogs",
    url: "/distributors",
    distributors: [],
  };

  let currentMenuLabel: string;
  let previousMenuLabel: string;

  const icons = {
    "Beer & Hard Seltzer": BeverageBottle,
    Wine: LiquorGlassBottleSolid,
    Spirits: MartiniSolid,
    "Non-Alcoholic": BeverageCan,
  };

  const toggleMenu = (node, label) => {
    // this just handles the close click event when the same tab is closed
    // handling this separately for outside click events

    return {
      destroy() {
        if (label == currentMenuLabel) {
          const closeMenuTrackingParams = [new GlobalNavMenuEntity({ label: currentMenuLabel })];
          trackClick("global_nav_menu_close", closeMenuTrackingParams);
          currentMenuLabel = previousMenuLabel = null;
        }
      },
    };
  };

  const handleMenuLinkClick = (item, label_type = null) => {
    if (!label_type) {
      if (item.category_id) {
        label_type = "category";
      } else if (item.subcategory_id) {
        label_type = "subcategory";
      }
    }

    currentMenuLabel = null;
    const trackingParams = [
      new CtaEntity({ url: href(item), label: item.label, label_type: label_type }),
    ];
    trackClick("global_nav_menu_link", trackingParams);
  };

  const handleMenuToggleClick = label => {
    previousMenuLabel = currentMenuLabel;
    // handle toggleMenu since outside clicks cannot be covered here
    if (label == previousMenuLabel) {
      return;
    }

    if (previousMenuLabel) {
      const closeMenuTrackingParams = [new GlobalNavMenuEntity({ label: currentMenuLabel })];
      trackClick("global_nav_menu_close", closeMenuTrackingParams);
    }

    const trackingParams = [new GlobalNavMenuEntity({ label: label })];
    trackClick("global_nav_menu_open", trackingParams);
    currentMenuLabel = label;
  };

  const distributorIsIntegrated = distributor => {
    return (
      distributor.integrated ||
      (distributor.seven_fifty_integration && distributor.uses_price_zones)
    );
  };

  distributorItem.distributors = $DistributorsStore
    .map(distributor => ({
      label: distributor.name,
      distributor_id: distributor.id,
      integrated: distributorIsIntegrated(distributor),
    }))
    .sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()));

  $: showLiquorLicenseInfoBar = isOnProductTypeHeaderPage($page) as boolean;
</script>

<GlobalNavMenu slot="nav" id="global-product-lines-nav">
  {#each menuItems.menus || [] as menu (menu.header.id)}
    <NavMenu
      label={menu.header.label}
      testId={`${menu.header.label}-drawer`}
      triggerClick={() => handleMenuToggleClick(menu.header.label)}
    >
      <span use:toggleMenu={menu.header.label} />
      {#each menu.menus as submenu}
        {#if !submenu.feature}
          <NavList>
            {#if submenu.items}
              {#each submenu.items as subitem, i}
                {#if subitem.bold}
                  <NavLink
                    variant="secondary"
                    href={href(subitem)}
                    semibold
                    on:click={() => handleMenuLinkClick(subitem)}
                  >
                    {subitem.label}
                  </NavLink>
                {:else}
                  <NavLink
                    variant="secondary"
                    href={href(subitem)}
                    on:click={() => handleMenuLinkClick(subitem)}
                  >
                    {subitem.label}
                  </NavLink>
                {/if}
              {/each}
            {/if}
          </NavList>
        {:else}
          <Divider variant="vertical" />
          <Flex column gap="8">
            <Text class="text-sm-semibold desktop:text-semibold">Features</Text>
            <NavList>
              {#each submenu.items as subitem}
                {#if submenu.feature}
                  <NavLink
                    variant="secondary"
                    href={`product_listing${subitem.url}`}
                    on:click={() => handleMenuLinkClick(subitem, "feature")}
                  >
                    {subitem.label}
                  </NavLink>
                {/if}
              {/each}
            </NavList>
          </Flex>
        {/if}
      {/each}

      <!-- bottom -->

      <NavLink
        variant="primary"
        href={href(menu.header)}
        slot="link"
        on:click={() => handleMenuLinkClick(menu.header, "type")}
      >
        <Icon iconName={icons[menu.header.label]} class="icon-sm desktop:icon-base" />
        Browse All {menu.header.label}
      </NavLink>
    </NavMenu>
  {/each}

  {#if distributorItem.distributors.length}
    <NavMenu
      distributor
      class="px-16"
      label={distributorItem.label}
      testId="distributor-catalog-drawer"
      triggerClick={() => handleMenuToggleClick(distributorItem.label)}
    >
      <span use:toggleMenu={distributorItem.label} />
      <NavList distributor>
        {#each distributorItem.distributors as distributor}
          {#if distributor.label}
            <NavLink
              variant="secondary"
              featured={distributor.integrated}
              href={href(distributor)}
              on:click={() => handleMenuLinkClick(distributor, "distributor")}
            >
              {distributor.label}
            </NavLink>
          {/if}
        {/each}
      </NavList>

      <NavLink
        variant="primary"
        href="/distributors"
        slot="link"
        on:click={() => handleMenuLinkClick(distributorItem, "distributors")}
      >
        <Icon iconName={DeliveryTruckSolid} class="icon-sm desktop:icon-base" />
        Browse All Distributors in {$CurrentUserStore.market.name}, {$CurrentUserStore.market.state}
      </NavLink>
    </NavMenu>
  {/if}
</GlobalNavMenu>
<LiquorLicenseInfoBar {showLiquorLicenseInfoBar} />
